import { fromHex } from 'viem'

import { Hash } from 'types/entities'

import { errors } from 'constants/errors'
import { etherNumbers } from 'constants/variables'

import { generateAddress, toChecksumAddress } from './crypto'

export async function sleep(ms: number) {
  return await new Promise((resolve) => setTimeout(resolve, ms))
}

export function generateId(): number {
  const base = 10
  const exponent = 3

  const date = Date.now() * Math.pow(base, exponent)
  const extra = Math.floor(Math.random() * Math.pow(base, exponent))
  return date + extra
}

export function getPaginationItems({
  boundaryCount = 1,
  total = 1,
  current,
  siblingCount = 1,
}: {
  boundaryCount?: number
  total: number
  current: number
  siblingCount: number
}) {
  //TODO refactor this
  const range = (start: number, end: number) => {
    const length = end - start + 1
    return Array.from({ length }, (_, i) => start + i)
  }

  const startPages = range(1, Math.min(boundaryCount, total))
  const endPages = range(Math.max(total - boundaryCount + 1, boundaryCount + 1), total)

  const siblingsStart = Math.max(
    Math.min(current - siblingCount, total - boundaryCount - siblingCount * 2 - 1),
    boundaryCount + 2,
  )

  const siblingsEnd = Math.min(
    Math.max(current + siblingCount, boundaryCount + siblingCount * 2 + 2),

    endPages.length > 0 ? endPages[0] - 2 : total - 1,
  )

  const getLeftSiblings = () => {
    if (siblingsStart > boundaryCount + 2) {
      return [null]
    }

    if (boundaryCount + 1 < total - boundaryCount) {
      return [boundaryCount + 1]
    }

    return []
  }

  const getRightSiblings = () => {
    if (siblingsEnd < total - boundaryCount - 1) {
      return [null]
    }

    if (total - boundaryCount > boundaryCount) {
      return [total - boundaryCount]
    }

    return []
  }

  return [...startPages, ...getLeftSiblings(), ...range(siblingsStart, siblingsEnd), ...getRightSiblings(), ...endPages]
}

type ConvertInput = {
  decimals: number | null
  symbol: string
  balance: string
  name: string | null
  address: string
}

export function convert(data: ConvertInput) {
  const token = {
    symbol: data.symbol,
    name: data.name || '',
    address: toChecksumAddress(data.address),
    decimals: Number(data.decimals || 0),
    balance: fromHex(data.balance as Hash, 'bigint').toString(),
  }

  return token
}

export function checkIsNumber(amount: string | number, decimals: number) {
  const regexp = new RegExp(`^[+]?(\\d+\\.?\\d{0,${decimals}})$`, 'g')
  const isValid = regexp.test(amount.toString())

  if (!isValid) {
    throw new Error(errors.validation.INVALID_AMOUNT)
  }
  return isValid
}

export function getRandomAddresses(count: number) {
  return Array.from({ length: count }, () => ({
    addr: generateAddress(),
    amount: BigInt(1),
  }))
}

export function findArrayObjectField<T extends object>(arr: T[], value: string): T[] {
  return arr.filter((obj) =>
    Object.values(obj).some((field) => typeof field === 'string' && field.toLowerCase().includes(value.toLowerCase())),
  )
}

export function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

export function sliceValue(value: number | string | bigint | undefined) {
  if (value) {
    return String(value).slice(0, 7)
  }
  return '0'
}

export function findMax(values: bigint[]) {
  return values.reduce(
    (acc, curr, index) => {
      const isGreaterThanAcc = curr > acc.highest
      if (isGreaterThanAcc) {
        acc.highest = curr
        acc.index = index
      }
      return acc
    },
    {
      highest: etherNumbers.BG_ZERO,
      index: 0,
    },
  )
}

export function getMedian<T>(arr: T[]) {
  return Math.floor(arr.length / 2)
}

export function arrayToList<T extends object>(arr: T[]) {
  return arr.map((elem) => Object.values(elem)).join('\n')
}

export function convertStringObjToBigInt(params: Record<string, string | undefined>) {
  return Object.entries(params).reduce<Record<string, bigint | undefined>>((acc, [key, value]) => {
    if (value) {
      acc[key] = BigInt(value)
    }
    return acc
  }, {})
}
